import classNames from "classnames";
import { UserContext } from "context";
import { isFeatureToggled, translate } from "lib";
import _ from "lodash";
import React, { memo, useContext } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Circle as Logo } from "v2/components/shared/icons";
import { useOpenTodosCount, usePendingProjectsCount } from "v2/hooks";
import Link from "./Link";
import SearchComponent from "v2/components/Search";
import tcpRobot from "../../../../images/tcp_robot.png";

const betas = [""];

/**
 * The orange count beside "Projects" — the pending projects waiting on THIS
 * user to run the scheduling wizard.
 *
 * Orange, not red: a pending project is work waiting to start, not something
 * that has gone wrong. Same #ff8400 as the "Pending Projects" dot on the
 * dashboard's Project Status card ($orange in _home.scss), so the two readings
 * of the same number look like the same thing.
 *
 * A fixed 16px circle rather than a padded pill, so it stays round for the one
 * and two digit counts this realistically shows. `title` carries what the
 * number means — the circle alone does not say it.
 */
const PendingBadge = ({ count }: { count: number }) => (
	<span
		className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-warning text-white text-10 font-semibold"
		title={`${count} ${translate("pending_projects")}`}
		data-testid="masthead-pending-projects"
	>
		{count}
	</span>
);

/**
 * The RED count beside "ToDos" (Sim, 24 Sep 2026): open ToDos assigned to this
 * user. Red where the pending-projects count is orange — a ToDo is something
 * you have been asked to do, a pending project is work waiting to start. Same
 * 16px circle, same hidden-at-zero rule.
 */
const TodosBadge = ({ count }: { count: number }) => (
	<span
		className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-error text-white text-10 font-semibold"
		title={`${count} ${translate("open")} ${translate("todos")}`}
		data-testid="masthead-open-todos"
	>
		{count}
	</span>
);

const PermissionBasedMast = (): string[] => {
	const { currentUser } = useContext(UserContext) as { cable: any; currentUser: tcpinpoint.User };
	let features = ["properties", "projects", "tasks", "todos", "portfolios"];
	// Filter features to only include those that are toggled for the current user
	features = features.filter((f) => !!isFeatureToggled(f, currentUser));
	// TEMPLATES IS GATED (Sim, 22 Sep 2026). The "templates" flag used to control
	// only the two "New … Workflow Template" toolbar buttons, which left everyone
	// able to see and open the whole Templates section. It now controls the nav
	// entry itself, and the screens refuse a direct URL — see components/Templates
	// List/Show.
	if (isFeatureToggled("templates", currentUser)) {
		features.push("templates");
	}
	// SLA Monitor is gated behind the sla-monitoring feature flag
	if (isFeatureToggled("sla-monitoring", currentUser)) {
		features.push("sla-monitor");
	}
	// Lease Agreements — the Yardi-side LeaseAgreement -> Amendment -> Project
	// tree, read only. Gated behind its own flag (User::FEATURE_FLAGS in tcp-api)
	// while the amendment ERD rolls out; V2::LeaseAgreementsController checks the
	// same flag, so hiding the entry and closing the endpoint stay in step.
	if (isFeatureToggled("lease-agreements", currentUser)) {
		features.push("lease-agreements");
	}
	features.push("executive-dashboard");
	let reportFeatures = ["tenancy-tracking-report", "team-analysis-report", "lease-to-open-report"];
	reportFeatures = reportFeatures.filter((f) => !!isFeatureToggled(f, currentUser));
	if (reportFeatures.length > 0) {
		features.push("reports");
	}

	// // Add settings if user is admin
	if (currentUser && (currentUser as any).app_admin) {
		features.push("settings");
	}

	return features;
}

const Masthead = ({ className }: { className?: string }) => {
	const features = PermissionBasedMast();
	const { currentUser } = useContext(UserContext) as { currentUser: tcpinpoint.User };
	const { pathname } = useLocation();
	// Gated on the session being known — the Masthead also paints inside
	// App.tsx's Suspense fallback, before there is anyone to count for.
	//
	// RE-READ ON EVERY NAVIGATION (`pathname`). The Masthead mounts once for the
	// whole session and never unmounts, so a count fetched only on mount goes
	// stale the moment anything changes it — finish the wizard and the badge
	// still shows the old number until a hard refresh, disagreeing with the
	// dashboard on the very same screen. Finalizing navigates (to
	// /projects/:id), so keying on the path covers it. One `per_page: 1` request
	// per page change is the cost.
	const pendingCount = usePendingProjectsCount(!!currentUser, pathname);
	// Same re-read-on-navigation rule. Only fetched when ToDos is in the nav.
	const openTodosCount = useOpenTodosCount(!!currentUser && _.includes(features, "todos"), pathname);

	return (
		<header className={classNames("nav h-20 bg-white flex", className)}>
			<div className="left flex flex-1 border-t-3 border-transparent">
				<NavLink to="/" className="hover:bg-transparent">
					<Logo className="p-1 ml-2 w-15 h-20" />
				</NavLink>
				<div className="features flex ml-1 h-20 items-center">
					{_.map(features, feature => {
						return (
							<Link
								href={"/" + feature}
								label={feature}
								className={feature === "tasks" ? "px-5" : "px-4"}
								beta={_.includes(betas, feature)}
								key={feature}
								// Hidden at zero rather than shown as a "0" circle: the
								// badge is a prompt to go and do something, and at zero
								// there is nothing to do.
								badge={
									feature === "projects" && pendingCount > 0 ? (
										<PendingBadge count={pendingCount} />
									) : feature === "todos" && openTodosCount > 0 ? (
										<TodosBadge count={openTodosCount} />
									) : undefined
								}
							/>
						);
					})}
					<SearchComponent />
					{process.env.NODE_ENV === "development" && (
						<div className="flex justify-center items-center ml-10">
							<div className=" bg-warning-light w-full px-3 text-center h-12 leading-12 text-black-80 font-bold text-20 uppercase whitespace-no-wrap">
								develop branch
							</div>
						</div>
					)}
				</div>
			</div>
      <div className="flex items-center ml-10 mr-5">
        <a href="https://tcpinpoint-learning.thinkific.com/communities/Q29tbXVuaXR5LTQ5NTM4/"
          target="_blank" 
          rel="noopener noreferrer"
          className="hover:bg-transparent">
            <div className="flex justify-end">
              <div className="w-full px-3 text-right h-12 leading-12 text-black-80 font-bold text-15 whitespace-no-wrap">
                Join our Learning Community!
              </div>
              <img src={tcpRobot} alt="TCP Thinkific" className="w-12 h-12"/>
            </div>
        </a>
      </div>
			<div className="right">
				<div className="flex border-t-3 border-transparent">
					<Link href="/signout" className="w-19" label="signout" hideLabel />
				</div>
			</div>
		</header>
	);
};

export default memo(Masthead);
